import { useState, useEffect, useCallback } from 'react';
import { Mail, Trash2 } from 'lucide-react';
import { useAuth } from '../AuthModule';
import AvatarWithDesignerBadge from './AvatarWithDesignerBadge';
import {
  API,
  AvatarFallback,
  toColorInputValue,
  getLighterBorderColor,
  getContrastingTextColor,
} from './profileHelpers';
import ProfileNavTopBar from '../ProfileNavTopBar';
import { useT } from '../../i18n/I18nModule';
import { loadingPageText } from '../../i18n/loadingPage';

function formatFriendshipAddedFi(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('fi-FI', { day: 'numeric', month: 'numeric', year: 'numeric' });
}

/** Short Finnish relative time for "lähetetty … sitten". */
function formatFriendRequestAgo(iso) {
  if (!iso) return '';
  const from = new Date(iso);
  if (Number.isNaN(from.getTime())) return '';
  const diffMs = Date.now() - from.getTime();
  if (diffMs < 0) return '';
  const sec = Math.floor(diffMs / 1000);
  if (sec < 45) return 'juuri nyt';
  const mins = Math.floor(sec / 60);
  if (mins < 60) return mins <= 1 ? 'minuutti sitten' : `${mins} minuuttia sitten`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return hrs === 1 ? 'tunti sitten' : `${hrs} tuntia sitten`;
  const days = Math.floor(hrs / 24);
  if (days < 14) return days === 1 ? 'vuorokausi sitten' : `${days} vuorokautta sitten`;
  return from.toLocaleDateString('fi-FI', { day: 'numeric', month: 'numeric', year: 'numeric' });
}

function friendCardSurfaceStyle(bubbleColorHex) {
  if (bubbleColorHex == null || String(bubbleColorHex).trim() === '') return undefined;
  try {
    return {
      backgroundColor: toColorInputValue(bubbleColorHex),
      borderColor: getLighterBorderColor(bubbleColorHex),
      color: getContrastingTextColor(bubbleColorHex),
    };
  } catch {
    return undefined;
  }
}

function ApprovedFriendCard({ row, profileHref, added, onDm, dmBusy, onRemoveFriend, removeBusy }) {
  const t = useT();
  const [avatarImgError, setAvatarImgError] = useState(false);
  const surfaceStyle = friendCardSurfaceStyle(row.bubbleColor);

  return (
    <li className="Friends-card">
      <div
        className={`Friends-card-surface${surfaceStyle ? ' Friends-card-surface--colored' : ''}`}
        style={surfaceStyle}
      >
        <button
          type="button"
          className="Button Friends-card-remove"
          disabled={removeBusy || dmBusy}
          title="Poista kaveri"
          aria-label={`Poista kaveri: ${row.displayName}`}
          onClick={() => onRemoveFriend(row.userId, row.displayName)}
        >
          <Trash2 size={18} aria-hidden className="Friends-card-remove-icon" />
        </button>
        <a className="Friends-card-main" href={profileHref}>
          <span className="Friends-card-avatar Friends-card-avatar-wrap" aria-hidden="true">
            <AvatarWithDesignerBadge designer={row.avatarDesigner} variant="forum">
              {row.avatarUrl && !avatarImgError ? (
                <img
                  src={row.avatarUrl}
                  alt=""
                  className="Friends-card-avatar-img"
                  onError={() => setAvatarImgError(true)}
                />
              ) : (
                <AvatarFallback
                  displayName={row.displayName}
                  username={row.username}
                  size="small"
                  bubbleColorHex={row.bubbleColor}
                />
              )}
            </AvatarWithDesignerBadge>
          </span>
          <span className="Friends-card-body">
            <span className="Friends-card-name">{row.displayName}</span>
            {added ? <span className="Friends-card-meta">lisätty {added}</span> : null}
          </span>
        </a>
        <button
          type="button"
          className="Button Friends-card-dm"
          disabled={dmBusy}
          onClick={() => onDm(row.userId)}
          aria-label={`Lähetä yksityisviesti: ${row.displayName}`}
          title="Lähetä yksityisviesti"
        >
          <Mail size={20} aria-hidden className="Friends-card-dm-icon" />
          <span className="Friends-card-dm-label">{t('profile.sendDm')}</span>
        </button>
      </div>
    </li>
  );
}

function FriendRequestCard({
  row,
  kind,
  profileHref,
  actionBusy,
  onApprove,
  onIgnore,
  onCancelOutgoing,
}) {
  const [avatarImgError, setAvatarImgError] = useState(false);
  const ago = formatFriendRequestAgo(row.requestedAt);
  const titleId = `friend-req-title-${kind}-${row.userId}`;

  return (
    <li className="Friends-request-card">
      <div className="Friends-request-card-surface">
        <a
          className="Friends-request-card-avatar-wrap"
          href={profileHref}
          aria-labelledby={titleId}
        >
          <AvatarWithDesignerBadge designer={row.avatarDesigner} variant="forum">
            {row.avatarUrl && !avatarImgError ? (
              <img
                src={row.avatarUrl}
                alt=""
                className="Friends-card-avatar-img"
                onError={() => setAvatarImgError(true)}
              />
            ) : (
              <AvatarFallback
                displayName={row.displayName}
                username={row.username}
                size="small"
                bubbleColorHex={row.bubbleColor}
              />
            )}
          </AvatarWithDesignerBadge>
        </a>
        <div className="Friends-request-card-main">
          {kind === 'incoming' ? (
            <p id={titleId} className="Friends-request-card-title">
              <span className="Friends-request-card-kicker">KAVERIPYYNTÖ</span> käyttäjältä{' '}
              <strong>{row.displayName}</strong>
            </p>
          ) : (
            <>
              <p id={titleId} className="Friends-request-card-title">
                <span className="Friends-request-card-kicker">KAVERIPYYNTÖ LÄHETETTY</span> {ago}
              </p>
              <p className="Friends-request-card-sub">käyttäjälle {row.displayName}</p>
            </>
          )}
          <div className="Friends-request-card-actions">
            {kind === 'incoming' ? (
              <>
                <button
                  type="button"
                  className="Button"
                  disabled={actionBusy}
                  onClick={() => onApprove(row.userId)}
                >
                  Hyväksy
                </button>
                <button
                  type="button"
                  className="Button Button-secondary"
                  disabled={actionBusy}
                  onClick={() => onIgnore(row.userId)}
                >
                  Ohita
                </button>
              </>
            ) : (
              <button
                type="button"
                className="Button Button-secondary"
                disabled={actionBusy}
                onClick={() => onCancelOutgoing(row.userId)}
              >
                Peruuta pyyntö
              </button>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}

export default function KaveritPage() {
  const { user } = useAuth();
  const t = useT();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [approved, setApproved] = useState([]);
  const [outgoingRequested, setOutgoingRequested] = useState([]);
  const [incomingRequested, setIncomingRequested] = useState([]);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [ignoreModalRequesterId, setIgnoreModalRequesterId] = useState(null);
  const [ignoreReasonDraft, setIgnoreReasonDraft] = useState('');
  const [dmOpeningFriendId, setDmOpeningFriendId] = useState(null);
  const [removingFriendId, setRemovingFriendId] = useState(null);

  const userIdStr = user?.id != null ? String(user.id).replace(/n$/, '') : '';

  const openFriendDm = useCallback(
    async (friendUserId) => {
      if (!userIdStr || !friendUserId) return;
      setDmOpeningFriendId(friendUserId);
      setActionError(null);
      try {
        const res = await fetch(`${API}/api/direct-messages/friend-thread`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: userIdStr, friendUserId }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          setActionError(typeof data.error === 'string' ? data.error : 'Viestin avaus epäonnistui.');
          return;
        }
        const mid = data.rootMessageId != null ? String(data.rootMessageId).replace(/n$/, '') : '';
        if (mid && typeof window !== 'undefined') {
          window.location.assign(`/mail/${mid}`);
        }
      } finally {
        setDmOpeningFriendId(null);
      }
    },
    [userIdStr],
  );

  const loadList = useCallback(() => {
    if (!userIdStr) return;
    setLoading(true);
    setError(null);
    fetch(`${API}/api/user-friends/list?userId=${encodeURIComponent(userIdStr)}`)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => {
        setApproved(Array.isArray(data?.approved) ? data.approved : []);
        setOutgoingRequested(Array.isArray(data?.outgoingRequested) ? data.outgoingRequested : []);
        setIncomingRequested(Array.isArray(data?.incomingRequested) ? data.incomingRequested : []);
      })
      .catch(() => {
        setError('Kaverilistan lataus epäonnistui.');
        setApproved([]);
        setOutgoingRequested([]);
        setIncomingRequested([]);
      })
      .finally(() => setLoading(false));
  }, [userIdStr]);

  useEffect(() => {
    if (!userIdStr) return;
    loadList();
  }, [userIdStr, loadList]);

  const respondApprove = async (requesterId) => {
    if (!userIdStr || !requesterId) return;
    setActionBusy(true);
    setActionError(null);
    try {
      const res = await fetch(`${API}/api/user-friends/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: userIdStr, requesterId, action: 'APPROVE' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setActionError(typeof data.error === 'string' ? data.error : 'Hyväksyntä epäonnistui.');
        return;
      }
      loadList();
    } finally {
      setActionBusy(false);
    }
  };

  const submitIgnore = async () => {
    const reason = ignoreReasonDraft.trim();
    const requesterId = ignoreModalRequesterId;
    if (!userIdStr || !requesterId || !reason) return;
    setActionBusy(true);
    setActionError(null);
    try {
      const res = await fetch(`${API}/api/user-friends/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: userIdStr,
          requesterId,
          action: 'IGNORE',
          ignoreReason: reason,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setActionError(typeof data.error === 'string' ? data.error : 'Hylkäys epäonnistui.');
        return;
      }
      setIgnoreModalRequesterId(null);
      setIgnoreReasonDraft('');
      loadList();
    } finally {
      setActionBusy(false);
    }
  };

  const cancelOutgoing = async (targetUserId) => {
    if (!userIdStr || !targetUserId) return;
    setActionBusy(true);
    setActionError(null);
    try {
      const res = await fetch(`${API}/api/user-friends/request`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: userIdStr, targetUserId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setActionError(typeof data.error === 'string' ? data.error : 'Peruutus epäonnistui.');
        return;
      }
      loadList();
    } finally {
      setActionBusy(false);
    }
  };

  const removeApprovedFriend = async (friendUserId, displayName) => {
    if (!userIdStr || !friendUserId) return;
    const label = (displayName || '').trim() || 'tämä käyttäjä';
    if (!window.confirm(`Poistetaanko ${label} kaverilistaltasi?`)) return;
    setRemovingFriendId(friendUserId);
    setActionError(null);
    const friendIdParam = String(friendUserId).replace(/n$/i, '');
    const qp = new URLSearchParams({ userId: userIdStr, friendUserId: friendIdParam });
    try {
      // Query string: some proxies strip DELETE bodies; server accepts query or body.
      const res = await fetch(`${API}/api/user-friends/approved?${qp}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: userIdStr, friendUserId: friendIdParam }),
      });
      const raw = await res.text();
      let data = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        data = {};
      }
      if (!res.ok) {
        const fromJson = typeof data.error === 'string' ? data.error : null;
        setActionError(
          fromJson
            ? fromJson
            : `Kaverin poisto epäonnistui (HTTP ${res.status}${raw && raw.length < 120 ? `: ${raw.trim()}` : ''}).`,
        );
        return;
      }
      loadList();
    } catch (e) {
      setActionError(typeof e?.message === 'string' ? e.message : 'Verkkovirhe — kaverin poisto epäonnistui.');
    } finally {
      setRemovingFriendId(null);
    }
  };

  if (!user) {
    return (
      <div className="App-block">
        <p>Kirjaudu sisään nähdäksesi kaverisi.</p>
      </div>
    );
  }

  return (
    <div className="App-block Friends-page">
      <div className="kaverit-container">
        <ProfileNavTopBar user={user} currentPage="friends" />
        {loading ? (
          <p>{loadingPageText(t, 'friends')}</p>
        ) : error ? (
          <p className="Inbox-error" role="alert">
            {error}
          </p>
        ) : (
          <>
            {actionError && (
              <p className="Friends-action-error" role="alert">
                {actionError}
              </p>
            )}
            <div className="Friends-requests-two-col">
              <section
                className="Friends-section Friends-section--requests-incoming"
                aria-labelledby="friends-incoming-requests-heading"
              >
                <h3 id="friends-incoming-requests-heading" className="Friends-kaverit-heading">
                  {t('friends.receivedRequests')}
                </h3>
                {incomingRequested.length === 0 ? (
                  <p className="Friends-empty">Ei odottavia pyyntöjä.</p>
                ) : (
                  <ul className="Friends-list Friends-list--request-cards">
                    {incomingRequested.map((row) => {
                      const profileHref = `/profiili/${encodeURIComponent(row.displayName)}`;
                      return (
                        <FriendRequestCard
                          key={`incoming-${row.userId}`}
                          row={row}
                          kind="incoming"
                          profileHref={profileHref}
                          actionBusy={actionBusy}
                          onApprove={respondApprove}
                          onIgnore={(uid) => {
                            setIgnoreModalRequesterId(uid);
                            setIgnoreReasonDraft('');
                          }}
                          onCancelOutgoing={cancelOutgoing}
                        />
                      );
                    })}
                  </ul>
                )}
              </section>

              <section
                className="Friends-section Friends-section--requests-outgoing"
                aria-labelledby="friends-outgoing-requests-heading"
              >
                <h3 id="friends-outgoing-requests-heading" className="Friends-kaverit-heading">
                  {t('friends.sentRequests')}
                </h3>
                {outgoingRequested.length === 0 ? (
                  <p className="Friends-empty">Ei lähetettyjä pyyntöjä.</p>
                ) : (
                  <ul className="Friends-list Friends-list--request-cards">
                    {outgoingRequested.map((row) => {
                      const profileHref = `/profiili/${encodeURIComponent(row.displayName)}`;
                      return (
                        <FriendRequestCard
                          key={`outgoing-${row.userId}`}
                          row={row}
                          kind="outgoing"
                          profileHref={profileHref}
                          actionBusy={actionBusy}
                          onApprove={respondApprove}
                          onIgnore={(uid) => {
                            setIgnoreModalRequesterId(uid);
                            setIgnoreReasonDraft('');
                          }}
                          onCancelOutgoing={cancelOutgoing}
                        />
                      );
                    })}
                  </ul>
                )}
              </section>
            </div>

          <section className="Friends-section Friends-section--approved" aria-labelledby="friends-approved-heading">
            <h3 id="friends-approved-heading" className="Friends-kaverit-heading">
              {t('friends.title')}
            </h3>
            {approved.length === 0 ? (
              <p className="Friends-empty">Ei hyväksyttyjä kavereita vielä.</p>
            ) : (
              <ul className="Friends-list Friends-list--cards">
                {approved.map((row) => {
                  const profileHref = `/profiili/${encodeURIComponent(row.displayName)}`;
                  const added = formatFriendshipAddedFi(row.friendshipCreatedAt);
                  const dmBusy = dmOpeningFriendId === row.userId;
                  const removeBusy = removingFriendId === row.userId;
                  return (
                    <ApprovedFriendCard
                      key={row.userId}
                      row={row}
                      profileHref={profileHref}
                      added={added}
                      onDm={openFriendDm}
                      dmBusy={dmBusy}
                      onRemoveFriend={removeApprovedFriend}
                      removeBusy={removeBusy}
                    />
                  );
                })}
              </ul>
            )}
            </section>
          </>
        )}

        {ignoreModalRequesterId && (
          <div className="FriendIgnoreModal-overlay" role="dialog" aria-modal="true" aria-labelledby="friends-ignore-title">
            <div className="FriendIgnoreModal">
              <h2 id="friends-ignore-title" className="FriendIgnoreModal-title">
                Ohituksen syy
              </h2>
              <p className="FriendIgnoreModal-hint">Kirjoita lyhyt syy (pakollinen).</p>
              <textarea
                className="FriendIgnoreModal-textarea"
                rows={4}
                value={ignoreReasonDraft}
                onChange={(e) => setIgnoreReasonDraft(e.target.value)}
                disabled={actionBusy}
              />
              <div className="FriendIgnoreModal-actions">
                <button
                  type="button"
                  className="Button Button-secondary"
                  onClick={() => {
                    setIgnoreModalRequesterId(null);
                    setIgnoreReasonDraft('');
                  }}
                  disabled={actionBusy}
                >
                  Peruuta
                </button>
                <button
                  type="button"
                  className="Button"
                  onClick={submitIgnore}
                  disabled={actionBusy || !ignoreReasonDraft.trim()}
                >
                  Tallenna ohitus
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
